import { ArrowLeft, Users, Mail, User, CheckCircle2, Plus, Save, X as XIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { apiUpdateTeamMembers } from '../../api/auth';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  grade: string;
  jobRole: string;
  status: 'Active' | 'Pending';
}

interface NewManageTeamPageProps {
  teamId: string;
  teamName: string;
  hackathonName: string;
  leaderName: string;
  leaderEmail: string;
  initialMembers: TeamMember[];
  maxTeamSize: number;
  onBack: () => void;
}

export function NewManageTeamPage({
  teamId,
  teamName,
  hackathonName,
  leaderName,
  leaderEmail,
  initialMembers,
  maxTeamSize,
  onBack,
}: NewManageTeamPageProps) {
  const { user, token } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>(initialMembers);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    grade: '',
    jobRole: '',
  });

  const handleAddMember = () => {
    if (!newMember.name || !newMember.email || !newMember.grade || !newMember.jobRole) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!newMember.email.endsWith("@capgemini.com")) {
      toast.error('Only Capgemini email addresses are accepted (e.g. example@capgemini.com)');
      return;
    }

    if (members.length + 1 >= maxTeamSize) {
      toast.error(`Maximum team size is ${maxTeamSize} members`);
      return;
    }

    const emailExists = members.some((m) => m.email === newMember.email) || leaderEmail === newMember.email;
    if (emailExists) {
      toast.error('Email already exists in the team');
      return;
    }

    const member: TeamMember = {
      id: Date.now().toString(),
      ...newMember,
      status: 'Pending',
    };

    setMembers([...members, member]);
    setNewMember({ name: '', email: '', grade: '', jobRole: '' });
    setShowAddForm(false);
    toast.success('Team member added successfully');
  };

  const handleRemoveMember = (id: string) => {
    setMembers(members.filter((m) => m.id !== id));
    toast.success('Team member removed');
  };

  const handleSaveChanges = async () => {
    if (!token) {
      toast.error("You must be logged in to save team changes.");
      return;
    }

    try {
      const normalize = (email: string) =>
        email.trim().toLowerCase();

      // ✅ Leader MUST be included for backend acceptance
      const leaderPayload = {
        name: leaderName.trim(),
        email: normalize(leaderEmail),
        grade: "",
        jobRole: "",
        status: "accepted",
        userId: user?.id
      };

      // ✅ Other members
      const payloadMembers = [
        leaderPayload,
        ...members.map(m => ({
          name: m.name.trim(),
          email: normalize(m.email),
          grade: m.grade.trim(),
          jobRole: m.jobRole.trim(),
          status: m.status.toLowerCase() === 'active' ? 'accepted' : m.status.toLowerCase()
        }))
      ];

      console.log("🚀 Backend leader email:", leaderPayload.email);
      console.log("🚀 Members being sent:", payloadMembers);
      console.log("🚀 Email normalization applied (trim + toLowerCase)");

      await apiUpdateTeamMembers(token, teamId, payloadMembers);

      toast.success("Team changes saved successfully");
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to save team changes");
    }
  };


  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#F7F9FC]">
        <div className="max-w-7xl mx-auto px-8 py-12">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-[#1A73E8] hover:text-[#1557B0] mb-6 font-medium transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to My Teams
            </button>
            <h1 className="font-medium text-[36px] text-[#1a1f36] mb-2 tracking-tight">Manage Team</h1>
            <p className="text-[#6e7891] text-[16px]">
              View and update your team details for this hackathon.
            </p>
          </div>

          {/* Team Overview Card */}
          <div className="bg-white rounded-2xl border border-[#e4e8ee] shadow-sm p-6 mb-5">
            {/* Team Name and Hackathon */}
            <div className="grid md:grid-cols-2 gap-5 pb-5 border-b border-[#e4e8ee]">
              <div>
                <label className="text-[#0058ab] text-[11px] font-medium uppercase tracking-wider block mb-2">
                  Team Name
                </label>
                <p className="text-[#1a1f36] text-[18px] font-medium">{teamName}</p>
              </div>
              <div>
                <label className="text-[#0058ab] text-[11px] font-medium uppercase tracking-wider block mb-2">
                  Hackathon
                </label>
                <p className="text-[#0058ab] text-[18px] font-medium">{hackathonName}</p>
              </div>
            </div>

            {/* Team Leader Section */}
            <div className="pt-5">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-4 h-4 text-[#0058ab]" />
                <label className="text-[#0058ab] text-[11px] font-medium uppercase tracking-wider">
                  Team Leader (Read-Only)
                </label>
              </div>
              <div className="bg-[#f5f7fb] rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-[#0058ab]" />
                  <span className="text-[#1a1f36] text-[14px] font-medium">{leaderName}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#0058ab]" />
                  <span className="text-[#0058ab] text-[14px]">{leaderEmail}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Team Size Rules Info */}
          <div className="bg-[#ebf8fd] border border-[rgba(29,184,242,0.2)] rounded-xl p-5 mb-5">
            <div className="flex gap-3">
              <div className="shrink-0 mt-0.5">
                <div className="w-5 h-5 rounded-full bg-[#1db8f2] bg-opacity-20 flex items-center justify-center">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="7" stroke="#00D5D0" strokeWidth="2" />
                    <path d="M8 4V8M8 11H8.01" stroke="#00D5D0" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </div>
              </div>
              <div>
                <h3 className="text-[#1a1f36] text-[14px] font-medium mb-3">Team Size Rules</h3>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#00D5D0] mt-0.5 shrink-0" />
                    <span className="text-[#1a1f36] text-[13px]">Minimum team size: 1 member</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#00D5D0] mt-0.5 shrink-0" />
                    <span className="text-[#1a1f36] text-[13px]">Maximum team size: {maxTeamSize} members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#00D5D0] mt-0.5 shrink-0" />
                    <span className="text-[#1a1f36] text-[13px]">All member emails must be unique</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Current Team Size Card */}
          <div
            className="rounded-2xl shadow-lg p-6 mb-6 text-white"
            style={{
              background: 'linear-gradient(90deg, rgb(0, 88, 171) 0%, rgb(29, 184, 242) 100%)'
            }}
          >
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5" />
              <label className="text-[11px] font-medium uppercase tracking-wider opacity-90">
                Current Team Size
              </label>
            </div>
            <div className="flex items-end gap-2">

              <span className="text-[48px] font-normal leading-none">{members.length+1}</span>
 
              <span className="text-[18px] opacity-90 mb-3">/ {maxTeamSize} members</span>
            </div>
          </div>

          {/* Add Team Member Button */}
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-[#00828e] text-white px-6 py-3 rounded-xl font-medium text-[14px] hover:bg-[#006b75] transition-colors duration-200 flex items-center gap-2 mb-6"
          >
            <Plus className="w-4 h-4" />
            Add Team Member
          </button>

          {/* Add Member Form */}
          {showAddForm && (
            <div className="bg-white border-l-4 border-[#F4B400] rounded-2xl shadow-sm p-6 mb-6">
              <h3 className="text-[#1a1f36] text-[18px] font-medium mb-5">Add New Member</h3>

              <div className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1a1f36] text-[13px] font-medium block mb-2">
                      Member Name
                    </label>
                    <input
                      type="text"
                      value={newMember.name}
                      onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                      className="w-full px-4 py-3 border border-[#e4e8ee] rounded-xl bg-white text-[#1a1f36] text-[14px] placeholder-[#8b93a8] focus:outline-none focus:border-[#0058ab] focus:ring-2 focus:ring-[#0058ab] focus:ring-opacity-20 transition-all"
                      placeholder="Enter full name"
                    />
                  </div>
                  <div>
                    <label className="text-[#1a1f36] text-[13px] font-medium block mb-2">
                      Member Email
                    </label>
                    <input
                      type="email"
                      value={newMember.email}
                      onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                      className="w-full px-4 py-3 border border-[#e4e8ee] rounded-xl bg-white text-[#1a1f36] text-[14px] placeholder-[#8b93a8] focus:outline-none focus:border-[#0058ab] focus:ring-2 focus:ring-[#0058ab] focus:ring-opacity-20 transition-all"
                      placeholder="Enter email address"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[#1a1f36] text-[13px] font-medium block mb-2">
                      Grade
                    </label>
                    <input
                      type="text"
                      value={newMember.grade}
                      onChange={(e) => setNewMember({ ...newMember, grade: e.target.value })}
                      className="w-full px-4 py-3 border border-[#e4e8ee] rounded-xl bg-white text-[#1a1f36] text-[14px] placeholder-[#8b93a8] focus:outline-none focus:border-[#0058ab] focus:ring-2 focus:ring-[#0058ab] focus:ring-opacity-20 transition-all"
                      placeholder="e.g., A1, B2"
                    />
                  </div>
                  <div>
                    <label className="text-[#1a1f36] text-[13px] font-medium block mb-2">
                      Job Role
                    </label>
                    <input
                      type="text"
                      value={newMember.jobRole}
                      onChange={(e) => setNewMember({ ...newMember, jobRole: e.target.value })}
                      className="w-full px-4 py-3 border border-[#e4e8ee] rounded-xl bg-white text-[#1a1f36] text-[14px] placeholder-[#8b93a8] focus:outline-none focus:border-[#0058ab] focus:ring-2 focus:ring-[#0058ab] focus:ring-opacity-20 transition-all"
                      placeholder="e.g., Developer, Designer"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleAddMember}
                    className="bg-[#0058ab] text-white px-6 py-3 rounded-xl font-medium text-[14px] hover:bg-[#004080] transition-colors duration-200"
                  >
                    Add Member
                  </button>
                  <button
                    onClick={() => {
                      setShowAddForm(false);
                      setNewMember({ name: '', email: '', grade: '', jobRole: '' });
                    }}
                    className="bg-white border border-[#e4e8ee] text-[#6e7891] px-6 py-3 rounded-xl font-medium text-[14px] hover:bg-[#f5f7fb] transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Team Members Table */}
          <div className="bg-white rounded-2xl border border-[#e4e8ee] shadow-sm overflow-hidden mb-6">
            <div className="p-6 border-b border-[#e4e8ee]">
              <h3 className="text-[#1a1f36] text-[18px] font-medium">Team Members ({members.length + 1})</h3>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#f5f7fb] border-b border-[#e4e8ee]">
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Grade
                    </th>
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Job Role
                    </th>
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-[#6e7891] text-[13px] font-medium uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e4e8ee]">
                  {members.map((member) => (
                    <tr key={member.id} className="hover:bg-[#f5f7fb] transition-colors duration-150">
                      <td className="px-6 py-4 text-[#1a1f36] text-[14px] font-medium">
                        {member.name}
                      </td>
                      <td className="px-6 py-4 text-[#0058ab] text-[14px]">
                        {member.email}
                      </td>
                      <td className="px-6 py-4 text-[#1a1f36] text-[14px]">
                        {member.grade}
                      </td>
                      <td className="px-6 py-4 text-[#1a1f36] text-[14px]">
                        {member.jobRole}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-[12px] font-medium ${member.status === 'Active'
                            ? 'bg-[#e6f4ea] text-[#1e8e3e]'
                            : 'bg-[#fef7e0] text-[#f9ab00]'
                            }`}
                        >
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="text-[#d93025] hover:text-[#b71c1c] transition-colors duration-200 flex items-center gap-1 text-[14px] font-medium"
                        >
                          <XIcon className="w-4 h-4" />
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                  {members.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-[#6e7891]">
                        No team members yet. Add members using the form above.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Save/Cancel Actions */}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onBack}
              className="bg-white border border-[#e4e8ee] text-[#6e7891] px-8 py-3 rounded-xl font-medium text-[14px] hover:bg-[#f5f7fb] transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              className="bg-[#1e8e3e] text-white px-8 py-3 rounded-xl font-medium text-[14px] hover:bg-[#188230] transition-colors duration-200 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}